import React, { useState } from 'react';
import {
    Button,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    CircularProgress,
} from '@mui/material';
import { Download, Language } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useGetCustomerOrderInvoice } from '../../hooks/customer/useGetCustomerOrderInvoice';

/**
 * Drop-in invoice download button for the customer side. Shows a
 * tiny menu with EN / AR — the active locale is highlighted; a
 * single click on the main button uses the active locale directly.
 */
const InvoiceDownloadButton = ({ orderId, size = 'small', variant = 'outlined' }) => {
    const { i18n, t } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { download, isFetching } = useGetCustomerOrderInvoice(orderId);

    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handlePick = (lang) => {
        handleClose();
        download(lang);
    };

    return (
        <>
            <Button
                size={size}
                variant={variant}
                startIcon={
                    isFetching ? <CircularProgress size={14} thickness={5} /> : <Download />
                }
                onClick={handleOpen}
                disabled={isFetching || !orderId}
                sx={{ textTransform: 'none', fontWeight: 600 }}
            >
                {t('orders.downloadInvoice', 'Download invoice')}
            </Button>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                <MenuItem
                    onClick={() => handlePick(isArabic ? 'ar' : 'en')}
                    sx={{ fontWeight: 700 }}
                >
                    <ListItemIcon><Download fontSize="small" /></ListItemIcon>
                    <ListItemText
                        primary={t('orders.invoiceCurrentLang', 'Current language')}
                        secondary={isArabic ? 'العربية' : 'English'}
                    />
                </MenuItem>
                <MenuItem onClick={() => handlePick('ar')}>
                    <ListItemIcon><Language fontSize="small" /></ListItemIcon>
                    <ListItemText primary="العربية" />
                </MenuItem>
                <MenuItem onClick={() => handlePick('en')}>
                    <ListItemIcon><Language fontSize="small" /></ListItemIcon>
                    <ListItemText primary="English" />
                </MenuItem>
            </Menu>
        </>
    );
};

export default InvoiceDownloadButton;
